import { createAtomObserver } from '@exodus/atoms'

const createLocalSeedBackupsPlugin = ({
  port,
  localSeedBackups,
  localSeedBackupsAtom,
  localSeedBackupMaxAgeAtom,
  wallet,
  config: { reasons, shouldBackup },
  logger = {},
}) => {
  const observers = [
    createAtomObserver({ port, atom: localSeedBackupsAtom, event: 'localSeedBackups' }),
    createAtomObserver({ port, atom: localSeedBackupMaxAgeAtom, event: 'localSeedBackupMaxAge' }),
  ]

  const onLoad = () => {
    observers.forEach((observer) => observer.start())
  }

  const onUnlock = async () => {
    await localSeedBackups.clearOldBackups()
  }

  const onPreImport = async ({ backupType, walletExists, forgotPassphrase }) => {
    if (!walletExists || forgotPassphrase) {
      logger.debug('Wallet does not exist or forgot passphrase. Skipping')
      return
    }

    if (!shouldBackup) {
      logger.debug('shouldBackup is not true. Skipping')
      return
    }

    if (backupType === 'passkeys' || backupType === 'v3') {
      logger.debug('Backup type is passkeys. Skipping')
      return
    }

    if (!reasons.includes('import')) {
      logger.debug('Not backing up for restart reason "import"')
      return
    }

    try {
      const mnemonic = await wallet.getMnemonic()
      await localSeedBackups.add({ mnemonic })
      logger.info('Created a local seed backup before import')
    } catch (e) {
      logger.error('Failed to create a local seed backup before import', e.message)
    }
  }

  const onRestart = async ({ reason, forgotPassphrase, backupType }) => {
    if (forgotPassphrase) {
      logger.info('Forgot passphrase, clearing local seed backups')
      await localSeedBackups.clear()
      return
    }

    if (!shouldBackup) {
      logger.debug('shouldBackup is not true. Skipping')
      return
    }

    if (backupType === 'passkeys') {
      logger.debug('Backup type is passkeys. Skipping')
      return
    }

    if (!reasons.includes(reason)) {
      logger.debug(`Not backing up for restart reason "${reason}"`)
      return
    }

    // Import case is handled by onPreImport (before wallet.import() replaces the mnemonic)
    if (reason === 'import') {
      return
    }

    try {
      const mnemonic = await wallet.getMnemonic()
      await localSeedBackups.add({ mnemonic })
      logger.info('Created a local seed backup')
    } catch (e) {
      logger.error('Failed to create a local seed backup', e.message)
    }
  }

  const onStop = () => {
    observers.forEach((observer) => observer.unregister())
  }

  const onCreate = async ({ backupType }) => {
    if (!shouldBackup) {
      logger.debug('shouldBackup is not true. Skipping')
      return
    }

    if (backupType === 'passkeys') {
      logger.debug('Backup type is passkeys. Skipping')
      return
    }

    try {
      const mnemonic = await wallet.getMnemonic()
      await localSeedBackups.add({ mnemonic })
      logger.info('Created a local seed backup')
    } catch (e) {
      logger.error('Failed to create a local seed backup', e.message)
    }
  }

  return { onLoad, onUnlock, onPreImport, onRestart, onStop, onCreate }
}

export default createLocalSeedBackupsPlugin
